import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from "react"
import type {
  ConfirmationOptions,
  ConfirmationState,
  ConfirmationContextValue,
} from "./confirmation.types"

let confirmationCount = 0

function generateId() {
  confirmationCount = (confirmationCount + 1) % Number.MAX_SAFE_INTEGER
  return `confirmation-${confirmationCount}`
}

const ConfirmationContext = createContext<ConfirmationContextValue | null>(null)

export type ConfirmationProviderProps = {
  children: ReactNode
}

export function ConfirmationProvider(props: ConfirmationProviderProps) {
  const { children } = props
  const [confirmation, setConfirmation] = useState<ConfirmationState | null>(
    null,
  )

  const confirm = useCallback((options: ConfirmationOptions) => {
    const id = options.id || generateId()
    setConfirmation({
      ...options,
      id,
      open: true,
      isLoading: false,
    })
  }, [])

  const dismiss = useCallback(() => {
    setConfirmation((prev) => (prev ? { ...prev, open: false } : null))
    // Clear after animation
    setTimeout(() => {
      setConfirmation(null)
    }, 150)
  }, [])

  const setLoading = useCallback((isLoading: boolean) => {
    setConfirmation((prev) => (prev ? { ...prev, isLoading } : null))
  }, [])

  const value = useMemo(
    () => ({
      confirmation,
      confirm,
      dismiss,
      setLoading,
    }),
    [confirmation, confirm, dismiss, setLoading],
  )

  return (
    <ConfirmationContext.Provider value={value}>
      {children}
    </ConfirmationContext.Provider>
  )
}

export function useConfirmation() {
  const context = useContext(ConfirmationContext)
  if (!context) {
    throw new Error(
      "useConfirmation must be used within a ConfirmationProvider",
    )
  }
  return context
}
